/**
 * 「调整打卡任务的顺序」卡片（2026-10-30）。
 *
 * ## 用户原话
 *
 * > "关于目标模块下面的打卡任务，在任务多了之后，小孩界面上看就会有点乱。比如有
 * >  七八个或者 10 个左右的目标，打卡时就会不知道主次，不知道轻重缓急。……
 * >  能不能对这些打卡任务进行排序？能不能通过拖放的方式进行排序？"
 *
 * ## 它做了什么
 *
 * 1. 从节点树里挑出**有启用中打卡任务**的节点（`lib/taskOrder.ts` 的纯函数）；
 * 2. 用 `DragOrderList` 让家长**按住 ⠿ 拖动**（也有上移/下移按钮，不用拖也能排）；
 * 3. 松手即保存：`PUT /students/:id/nodes/order` → 写 `node.sort_order`
 *    → 孩子端「今天要做什么」立刻按新顺序显示。
 *
 * ## 为什么不需要迁移
 *
 * 孩子端的排序**本来就是** `ORDER BY node.sort_order, node.created_at`
 * （`checkins.service.ts` 的 `studentToday`），这一列一直存在、只是从没有写入口。
 *
 * ## 三态
 *
 * - 节点树没拿到 / 加载中 → 不渲染这块（父页面自己会显示骨架/报错）；
 * - **可排序的任务 < 2 个** → 整块不渲染（只有一个任务时"排序"没有意义）；
 * - 没有权限（家长 viewer） → 不渲染。
 */
import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader } from '../ui/Card';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Toast';
import { toErrorMessage } from '../../lib/api/error';
import { reorderHabits } from '../../lib/api/nodes';
import { domainKeys } from '../../lib/domainQueries';
import { orderableHabitsOf } from '../../lib/taskOrder';
import { DragOrderList, type DragOrderItem } from './DragOrderList';
import type { NodeSummary } from '../../lib/api/types';

export interface TaskOrderCardProps {
  studentId: string;
  tree: readonly NodeSummary[];
  /** 家长 owner/editor 才能排（服务端也会判）。 */
  canEdit: boolean;
}

export function TaskOrderCard({ studentId, tree, canEdit }: TaskOrderCardProps) {
  const toast = useToast();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const tasks = useMemo(() => orderableHabitsOf(tree), [tree]);

  const saveMutation = useMutation({
    mutationFn: (ids: string[]) => reorderHabits(studentId, ids),
    onSuccess: async (result) => {
      toast.showToast(`顺序已保存（${result.updated} 项）`, 'success');
      /**
       * 两处都要失效：家长端的节点树（顺序变了）+ 孩子端的今日清单
       * （它才是"孩子看到的顺序"）。
       */
      await qc.invalidateQueries({ queryKey: domainKeys.nodeTree(studentId) });
      await qc.invalidateQueries({ queryKey: domainKeys.today(studentId) });
    },
    onError: (err: unknown) => toast.showToast(`保存顺序失败：${toErrorMessage(err)}`, 'error'),
  });

  if (!canEdit || tasks.length < 2) return null;

  const items: DragOrderItem[] = tasks.map((task) => ({
    id: task.id,
    title: task.name,
    subtitle:
      task.goalName === ''
        ? task.nodeName
        : `${task.goalName}${task.nodeName === task.goalName ? '' : ` · ${task.nodeName}`}`,
  }));

  return (
    <Card>
      {/*
        ⚠️ 这一段文案是**用户逐字给的**（2026-10-31），不要改写、不要调整顺序。
        位置就是**按钮上方**（卡片标题下面）。

        ⚠️ 展开后按钮**下方**那句「拖完自动保存；也可以用右边的 ↑ ↓ 一点点挪。」
        是**用户明确要保留的**（2026-10-31："这句话要的，我没说要删呐"）——
        别再删它。
      */}
      <CardHeader
        title="调整打卡任务的顺序"
        description="任务多了以后，孩子容易分不清先做哪个 —— 点下面的「开始调整」把它们按你希望的顺序排好。孩子在「今天要做什么」里就按这个顺序看到。"
        actionAlign="start"
        action={
          <div className="flex basis-full flex-col items-start gap-2">
            {/*
              样式与下面「看一下孩子端」那颗**完全一致**（2026-10-31 用户要求：
              "一个有底色，一个没底色，好像按钮的高度也不一样"），
              并且两颗都 `fullWidth` —— 于是**长度也完全一样**（同一轮的第二个要求）。
            */}
            <Button variant="secondary" fullWidth onClick={() => setOpen((v) => !v)}>
              {open ? '收起' : `开始调整（${tasks.length} 项）`}
            </Button>
            {open && (
              <span className="text-xs text-slate-500">
                拖完自动保存；也可以用右边的 ↑ ↓ 一点点挪。
              </span>
            )}
          </div>
        }
      />

      {open && (
        <DragOrderList
          items={items}
          disabled={saveMutation.isPending}
          onReorder={(ids) => saveMutation.mutate(ids)}
        />
      )}
    </Card>
  );
}
